import { useEffect, useState, useRef } from 'react'
import { useParams } from '@reach/router'
import { Tree, ITreeNode } from '@blueprintjs/core'
import { cloneDeep, isEmpty } from 'lodash'
import { useUser } from '@hooks'
import { IPortfolioRegion } from '@api'
import {
  LocationTreeNodeT,
  mapFavoriteTreeContents,
  getFavoriteFloorsFromRegions,
  forEachNode,
} from './utils'
import { mapFavoriteFloorNodes } from './utils/mapFavoriteTreeContents'

const useFavoritesTreeContents = (regions: IPortfolioRegion[]) => {
  const { user } = useUser()
  const { floorId: paramsFloorId } = useParams()
  const [favoriteTreeNodes, setFavoriteTreeNodes] = useState<LocationTreeNodeT[]>([])
  const isFavoriteTreeNodesInitializedRef = useRef(false)

  // it's necessary to mutate the state
  const clonedFavoriteTreeNodes = cloneDeep(favoriteTreeNodes)
  const favoriteFloors = user?.context.settings?.favoriteFloors || []

  const setFavoriteContents = (nodePath: number[], callback: (node: ITreeNode) => void): void => {
    // it mutates the clonedLocationTreeNodes object
    callback(Tree.nodeFromPath(nodePath, clonedFavoriteTreeNodes))

    setFavoriteTreeNodes(clonedFavoriteTreeNodes)
  }

  const setFavoriteContentsForEachNode = (callback: (node: LocationTreeNodeT) => void): void => {
    // it mutates the clonedLocationTreeNodes object
    forEachNode(callback, clonedFavoriteTreeNodes)

    setFavoriteTreeNodes(clonedFavoriteTreeNodes)
  }

  const updateFavoriteTreeByFavoriteFloors = (favoriteFloors: string[]) => {
    const floors = getFavoriteFloorsFromRegions(regions, favoriteFloors)

    if (clonedFavoriteTreeNodes[0]?.childNodes) {
      clonedFavoriteTreeNodes[0].childNodes = mapFavoriteFloorNodes(floors, paramsFloorId)

      setFavoriteTreeNodes(clonedFavoriteTreeNodes)
    }
  }

  const collapseFavorites = () => {
    if (clonedFavoriteTreeNodes[0].isExpanded) {
      clonedFavoriteTreeNodes[0].isExpanded = false
      setFavoriteTreeNodes(clonedFavoriteTreeNodes)
    }
  }

  // init favoriteTreeNodes states
  useEffect(() => {
    if (isFavoriteTreeNodesInitializedRef.current || isEmpty(regions)) {
      return
    }

    setFavoriteTreeNodes(mapFavoriteTreeContents(regions, paramsFloorId, favoriteFloors))
    isFavoriteTreeNodesInitializedRef.current = true
  }, [
    setFavoriteTreeNodes,
    regions,
    favoriteFloors,
    paramsFloorId,
    isFavoriteTreeNodesInitializedRef,
  ])

  return {
    favoriteTreeNodes,
    collapseFavorites,
    setFavoriteContents,
    setFavoriteContentsForEachNode,
    updateFavoriteTreeByFavoriteFloors,
  }
}

export default useFavoritesTreeContents
